import React, { useState, useEffect, useCallback } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import styled from "styled-components";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { isValidPhoneNumber } from "libphonenumber-js";
import { auth, RecaptchaVerifier } from "@/app/firebaseClient";
import { signInWithPhoneNumber } from "firebase/auth";
import { useTheme } from "@mui/material/styles";

const StyledPhoneInput = styled(PhoneInput)`
  .react-tel-input {
    width: 100% !important;
  }
  .form-control {
    height: 42px !important;
    border-radius: 50px !important;
    font-size: 18px !important;
    border: 1px solid #c4c4c4 !important;
    padding-left: 60px !important;
  }
  .flag-dropdown {
    border: none !important;
  }
  .country-list {
    max-height: 200px;
    overflow-y: auto;
    scrollbar-width: 1px;
  }
`;

const PhoneNumberForm = ({
  countryCode,
  onPhoneSubmitted,
  setErrorMessage,
  isLoading,
  setIsLoading,
}) => {
  const theme = useTheme();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (!recaptchaVerifier) {
      const verifier = new RecaptchaVerifier(
        auth,
        "phone-recaptcha-container",
        {
          size: "invisible",
          callback: () => console.log("reCAPTCHA resolved."),
        }
      );
      verifier.render().then((widgetId) => {
        setRecaptchaWidgetId(widgetId);
        setRecaptchaVerifier(verifier);
      });
    }
    return () => {
      if (recaptchaVerifier) {
        recaptchaVerifier.clear();
        setRecaptchaVerifier(null);
      }
    };
  }, [recaptchaVerifier]);

  const handleSendCode = useCallback(
    async (event) => {
      event.preventDefault();
      if (hasSubmitted) return;
      setHasSubmitted(true);
      setIsLoading(true);
      setErrorMessage("");
      if (!isValidPhoneNumber("+" + phoneNumber)) {
        setErrorMessage("Please enter a valid phone number.");
        setIsLoading(false);
        setHasSubmitted(false);
        return;
      }
      if (!recaptchaVerifier) {
        setErrorMessage("reCAPTCHA is not ready yet. Please try again.");
        setIsLoading(false);
        setHasSubmitted(false);
        return;
      }
      try {
        if (recaptchaWidgetId !== null) {
          window.grecaptcha.reset(recaptchaWidgetId);
        }
        const confirmationResult = await signInWithPhoneNumber(
          auth,
          "+" + phoneNumber,
          recaptchaVerifier
        );
        window.confirmationResult = confirmationResult;
        onPhoneSubmitted("+" + phoneNumber);
      } catch (error) {
        setErrorMessage("Failed to send code. Please try again.");
        setHasSubmitted(false); // Reset on error
      } finally {
        setIsLoading(false);
      }
    },
    [
      phoneNumber,
      recaptchaVerifier,
      recaptchaWidgetId,
      onPhoneSubmitted,
      setIsLoading,
      setErrorMessage,
      hasSubmitted,
    ]
  );

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSendCode(e);
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [handleSendCode]);

  return (
    <form onSubmit={handleSendCode}>
      <div id="phone-recaptcha-container"></div>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          gap: { xs: 2, sm: 0 },
        }}
      >
        <StyledPhoneInput
          country={countryCode?.toLowerCase()}
          value={phoneNumber}
          onChange={(value) => setPhoneNumber(value)}
          inputProps={{
            name: "phone",
            required: true,
            autoFocus: true,
            placeholder: "Enter your mobile number",
          }}
          specialLabel=""
          containerStyle={{ width: "100%" }}
          inputStyle={{
            width: "100%",
            height: "50px",
            borderRadius: "40px",
            fontSize: "18px",
          }}
          dropdownStyle={{ maxHeight: "90px" }}
          disableDropdownScroll={false}
          excludeCountries={["cu", "ir", "kp", "so", "sy", "ye", "af"]}
          preferredCountries={["us", "ca"]}
        />
        <Button
          type="submit"
          variant="contained"
          size="medium"
          color="primary"
          disabled={isLoading || !phoneNumber || hasSubmitted}
          sx={{
            ml: { xs: 0, sm: 3 },
            borderRadius: "50px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: {
              xs: "48px",
              md: "auto",
            },
            width: { xs: "100%", sm: "280px" },
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <PhoneIphoneIcon fontSize="small" />
          )}
          <Box component="span" sx={{ flexGrow: 1, textAlign: "center" }}>
            {isLoading ? "Sending..." : "Send Code"}
          </Box>
        </Button>
      </Box>
    </form>
  );
};

export default PhoneNumberForm;
